// ─── Impostazioni → Chiavi del ponte (voce 20) ───────────────────────────────
//
// 🗣️ 24/09/2026: *«bisogna creare PMO_API_KEY: va generata sul gestionale di
// test, in /settings/api-keys. Il gestionale la mostra una volta sola.»*
//
// ⇒ Il server c'era già (⇒ `apps/api/src/modules/settings/chiavi.routes.ts`):
//   mancava la schermata da cui la segreteria la crea, la vede usata e la
//   revoca. Il disegno è quello del mockup approvato
//   (`mockup/chiavi-del-ponte-mockup.html`).
//
// 🔒⭐⭐ **LA CHIAVE IN CHIARO VIVE SOLO NELLO STATO DI QUESTO RIQUADRO.** Non
// entra nella cache delle richieste, non si scrive nel browser, e chiuso il
// riquadro non c'è più da nessuna parte: il gestionale ne conserva l'impronta,
// non la chiave. ⇒ Chi la perde ne crea un'altra e revoca questa.
//
// ⚖️ «Il ponte è spento» si deduce dall'elenco, e non è un'approssimazione: il
//    server risponde `503 BRIDGE_DISARMED` esattamente quando non c'è nessuna
//    chiave con `revocataIl` vuoto (⇒ `plugins/autenticazione.ts`).

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ETICHETTE_TIPO_DI_CHIAVE,
  TIPI_DI_CHIAVE,
  type ChiaveAppenaConiata,
  type ChiaveDelPonte,
  type TipoDiChiave,
} from '@pmo/shared';
import { chiama } from '../../lib/api.js';
import { istanteInItaliano as quando } from '../../lib/time.js';
import { Attesa, Guasto } from '../../components/pezzi.js';
import { usaModifica } from './comune.js';

const CHIAVE_ELENCO = 'chiavi-del-ponte';

/** La regola del server, ripetuta qui solo per spegnere il bottone: il freno vero è lo schema. */
const NOME_MINIMO = 3;

/** «creata oggi alle 15:00», ma «creata il 22/09/2026 18:40»: l'articolo solo davanti a una data. */
const conArticolo = (iso: string): string => {
  const s = quando(iso);
  return s.startsWith('oggi') ? s : `il ${s}`;
};

export function Chiavi(): React.JSX.Element {
  const puòModificare = usaModifica('manage_users');
  const qc = useQueryClient();
  const [coniata, setConiata] = useState<Coniata | null>(null);
  const [chiedeRevoca, setChiedeRevoca] = useState<string | null>(null);
  const [stato, setStato] = useState('');

  const elenco = useQuery({
    queryKey: [CHIAVE_ELENCO],
    queryFn: () => chiama<{ items: ChiaveDelPonte[] }>('GET', '/settings/api-keys'),
  });
  const rileggi = () => void qc.invalidateQueries({ queryKey: [CHIAVE_ELENCO] });

  const revoca = useMutation({
    meta: { fatto: (_d, k) => `chiave «${(k as ChiaveDelPonte).nome}» revocata` },
    mutationFn: (k: ChiaveDelPonte) => chiama('POST', `/settings/api-keys/${k.id}/revoke`),
    onSuccess: (_x, k) => {
      setChiedeRevoca(null);
      setStato(`Revocata: «${k.nome}» non apre più niente.`);
      rileggi();
    },
  });

  if (elenco.isLoading) return <Attesa cosa="le chiavi" />;
  if (elenco.error) return <Guasto errore={elenco.error} />;

  const chiavi = elenco.data?.items ?? [];
  // 🤖 22/10/2026: le chiavi di Duet non tengono acceso il ponte del bot, e il ponte spento si dice per lui.
  const vive = chiavi.filter((k) => k.revocataIl === null && k.tipo === 'ponte');

  return (
    <section className="chiavi">
      <header className="testata">
        <h2>Chiavi del ponte</h2>
        <p className="tenue piccolo">
          Le chiavi con cui il <strong>bot dei soci</strong> e <strong>Duet</strong> entrano nel gestionale.
          Una chiave di Duet apre solo le porte di Duet: legge, e scrive proposte che aspettano chi approva.
          Ogni chiave si vede <strong>una volta sola</strong>, quando la crei: il gestionale non la conserva e
          nessuno potrà rileggerla.
        </p>
        {!puòModificare && <p className="tenue">Questo accesso può guardare, non cambiare.</p>}
      </header>

      {vive.length === 0 && (
        <p className="ponte-spento">
          <strong>Il ponte è spento.</strong> Non c’è nessuna chiave in uso: il bot dei soci non può entrare,
          e ai soci risponde che non riesce a raggiungere il circolo.
        </p>
      )}

      {chiavi.length === 0 ? (
        <p className="tenue">Non è mai stata creata nessuna chiave.</p>
      ) : (
        <ul className="elenco-chiavi">
          {chiavi.map((k) => (
            <li key={k.id} className={k.revocataIl ? 'revocata' : ''}>
              <div className="chi">
                <strong>{k.nome}</strong>
                <code>{k.mostrata}</code>{' '}
                <span className="tipo-chiave">{ETICHETTE_TIPO_DI_CHIAVE[k.tipo]}</span>{' '}
                {k.revocataIl ? (
                  <span className="stato-chiave spenta">revocata</span>
                ) : (
                  <span className="stato-chiave viva">in uso</span>
                )}
                <div className="dati">
                  <span>creata {conArticolo(k.creataIl)}</span>
                  <span>ultimo uso: {k.ultimoUsoIl ? quando(k.ultimoUsoIl) : 'mai'}</span>
                  {k.revocataIl && <span>revocata {conArticolo(k.revocataIl)}</span>}
                </div>
              </div>
              {puòModificare && !k.revocataIl && chiedeRevoca !== k.id && (
                <button
                  type="button"
                  className="bordato pericolo"
                  onClick={() => {
                    revoca.reset();
                    setStato('');
                    setChiedeRevoca(k.id);
                  }}
                >
                  Revoca
                </button>
              )}
              {chiedeRevoca === k.id && (
                <div className="conferma">
                  <p className="tenue piccolo">
                    Da questo momento <strong>chi usa «{k.nome}» resta fuori</strong>, e non si può
                    riaccendere: se serve, se ne crea una nuova.
                    {/* ⚠️ Revocare l'ultima spegne il ponte: il bot dei soci smette
                        di rispondere a tutti, ed è il caso da dire prima. */}
                    {k.tipo === 'ponte' && vive.length === 1 && (
                      <>
                        {' '}
                        ⚠️ È l’<strong>unica chiave in uso</strong>: revocandola il ponte si spegne e il bot
                        dei soci smette di funzionare finché non ne metti una nuova.
                      </>
                    )}
                  </p>
                  <Guasto errore={revoca.error} />
                  <div className="riga-bottoni">
                    <button
                      type="button"
                      className="pericolo"
                      disabled={revoca.isPending}
                      onClick={() => revoca.mutate(k)}
                    >
                      {revoca.isPending ? 'Un attimo…' : 'Sì, revoca'}
                    </button>
                    <button type="button" className="bordato" onClick={() => setChiedeRevoca(null)}>
                      Lascia stare
                    </button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {coniata ? (
        <AppenaConiata
          coniata={coniata}
          chiudi={() => {
            setConiata(null);
            setStato('Chiave creata. Da qui in poi se ne vede solo l’inizio.');
          }}
        />
      ) : (
        puòModificare && (
          <Nuova
            nata={(c) => {
              setChiedeRevoca(null);
              setStato('');
              setConiata(c);
              rileggi();
            }}
          />
        )
      )}

      <p className="stato-salva fatto" aria-live="polite">
        {stato}
      </p>
    </section>
  );
}

type Coniata = ChiaveAppenaConiata & { nome: string; tipo: TipoDiChiave };

function Nuova({ nata }: { nata: (c: Coniata) => void }): React.JSX.Element {
  const [nome, setNome] = useState('');
  const [tipo, setTipo] = useState<TipoDiChiave>('ponte');
  const crea = useMutation({
    meta: { fatto: 'chiave creata: copiala adesso, dopo non si vede più' },
    mutationFn: (n: string) => chiama<ChiaveAppenaConiata>('POST', '/settings/api-keys', { nome: n, tipo }),
    onSuccess: (c, n) => nata({ ...c, nome: n, tipo }),
  });
  const pulito = nome.trim();

  return (
    <>
      <h3 className="titolo-sezione">Crea una chiave</h3>
      <form
        className="nuova-chiave"
        onSubmit={(e) => {
          e.preventDefault();
          if (pulito.length >= NOME_MINIMO) crea.mutate(pulito);
        }}
      >
        <label className="campo">
          <span>A cosa serve</span>
          <input
            value={nome}
            maxLength={80}
            placeholder="per esempio: Bot dei soci — gestionale di prova"
            onChange={(e) => setNome(e.target.value)}
          />
        </label>
        <label className="campo">
          <span>Per chi</span>
          <select value={tipo} onChange={(e) => setTipo(e.target.value as TipoDiChiave)}>
            {TIPI_DI_CHIAVE.map((t) => (
              <option key={t} value={t}>
                {t === 'ponte' ? '🔌 ponte del bot (prenota, disdice, sposta)' : '🤖 Duet (legge, e propone)'}
              </option>
            ))}
          </select>
        </label>
        <button type="submit" disabled={pulito.length < NOME_MINIMO || crea.isPending}>
          {crea.isPending ? 'Un attimo…' : 'Crea la chiave'}
        </button>
      </form>
      <Guasto errore={crea.error} />
      <p className="tenue piccolo">
        Il nome serve fra sei mesi: una chiave che non si sa a cosa serva non la revoca nessuno, per paura di
        spegnere qualcosa.
      </p>
    </>
  );
}

/**
 * 🔑 L'unica volta in cui la chiave esiste fuori dalla testa di chi la riceve.
 *
 * ⚠️ «Copia» può non riuscire — un browser che nega gli appunti, una pagina
 *    dentro una cornice — e allora lo si dice invece di scrivere «copiata»: la
 *    chiave resta comunque tutta selezionabile col mouse.
 */
function AppenaConiata({ coniata, chiudi }: { coniata: Coniata; chiudi: () => void }): React.JSX.Element {
  const [copia, setCopia] = useState<'no' | 'fatto' | 'negata'>('no');

  return (
    <div className="appena-coniata" role="region" aria-label="La chiave appena creata">
      <p>
        <strong>Ecco la chiave «{coniata.nome}».</strong> Copiala adesso e mettila nella configurazione{' '}
        {coniata.tipo === 'duet' ? 'di Duet' : 'del bot dei soci, alla voce '}
        {coniata.tipo === 'ponte' && <code>PMO_API_KEY</code>}. <strong>Non la rivedrai più</strong>: chiuso
        questo riquadro, il gestionale non sa più mostrarla.
      </p>
      <div className="la-chiave">{coniata.chiara}</div>
      <div className="riga-bottoni">
        <button
          type="button"
          onClick={() => {
            // ⚠️ Dentro un `async` anche il caso in cui gli appunti non esistono
            //    affatto (pagina non sicura) finisce nel `catch`, invece di
            //    rompere il clic.
            void (async () => {
              try {
                await navigator.clipboard.writeText(coniata.chiara);
                setCopia('fatto');
              } catch {
                setCopia('negata');
              }
            })();
          }}
        >
          {copia === 'fatto' ? 'Copiata ✓' : 'Copia'}
        </button>
        <button type="button" className="bordato" onClick={chiudi}>
          L’ho messa nel bot, chiudi
        </button>
      </div>
      {copia === 'negata' && (
        <p className="guasto" role="alert">
          Il browser non mi lascia copiare da qui: selezionala col mouse e copiala a mano.
        </p>
      )}
      <p className="tenue piccolo">
        ⛔ Non incollarla in una chat, in una email o in un messaggio: chi la legge entra nel gestionale come
        {coniata.tipo === 'duet' ? ' Duet' : ' il bot'}. Se succede, revocala e creane un’altra.
      </p>
    </div>
  );
}
